import React from 'react';
import { Grid } from '@material-ui/core';
import { useCommonStyles } from 'components/UI/CommonStyles';
import { useSingleSaleTableListStyles } from 'components/Payments/SingleSale/Table/List/SingleSaleTableList.styles';
import { SingleSaleTableItem } from 'components/Payments/SingleSale/Table/Item/SingleSaleTableItem';
import { GenericOrderItem } from 'components/Payments/SingleSale/SingleSale';
import { FieldError } from '@spike/model';
import { AddOnServiceOrderItem, CustomOrderItem, Order, ProductOrderItem } from '@spike/order-model';
import clsx from 'clsx';

interface ItemsTableProps {
    order: Order;
    calculating?: boolean;
    errors?: Array<FieldError>;
    className?: string;
    onChange: (orderItems: Order['items']) => void;
}

export const SingleSaleTableList: React.FC<ItemsTableProps> = props => {
    const commonClasses = useCommonStyles();
    const classes = useSingleSaleTableListStyles();

    const orderItems = React.useMemo<Array<GenericOrderItem>>(() => {
        return [...props.order.items.products, ...props.order.items.addOnServices, ...props.order.items.customs];
    }, [props.order.items]);

    const orderProductIds = React.useMemo(() => {
        return props.order.items.products.map(item => item.product?.businessProduct?.id).filter(id => id !== undefined);
    }, [props.order.items.products]);

    const orderAddOnServiceIds = React.useMemo(() => {
        return props.order.items.addOnServices.map(item => item.addOnService?.id).filter(id => id !== undefined);
    }, [props.order.items.addOnServices]);

    const changeHandler = (updatedLine: GenericOrderItem) => {
        props.onChange({
            appointments: [],
            products: props.order.items.products.map(item =>
                item.orderItem.uuid === updatedLine.orderItem.uuid ? (updatedLine as ProductOrderItem) : item
            ),
            addOnServices: props.order.items.addOnServices.map(item =>
                item.orderItem.uuid === updatedLine.orderItem.uuid ? (updatedLine as AddOnServiceOrderItem) : item
            ),
            customs: props.order.items.customs.map(item =>
                item.orderItem.uuid === updatedLine.orderItem.uuid ? (updatedLine as CustomOrderItem) : item
            )
        });
    };

    const removeHandler = (uuid: string) => {
        props.onChange({
            appointments: [],
            products: props.order.items.products.filter(item => item.orderItem.uuid !== uuid),
            addOnServices: props.order.items.addOnServices.filter(item => item.orderItem.uuid !== uuid),
            customs: props.order.items.customs.filter(item => item.orderItem.uuid !== uuid)
        });
    };

    return (
        <>
            <div className={clsx(classes.container, props.className)}>
                <Grid
                    container
                    className={classes.gridHeader}
                >
                    <Grid
                        item
                        xs={3}
                        className={commonClasses.typo15_600}
                    >
                        Item
                    </Grid>
                    <Grid
                        item
                        xs={1}
                        className={clsx(commonClasses.typo15_600, classes.gridCenter)}
                    >
                        Type
                    </Grid>
                    <Grid
                        item
                        xs={1}
                        className={clsx(commonClasses.typo15_600, classes.gridCenter)}
                    >
                        Quantity
                    </Grid>
                    <Grid
                        item
                        xs={2}
                        className={clsx(commonClasses.typo15_600, classes.gridCenter)}
                    >
                        Discount
                    </Grid>
                    <Grid
                        item
                        xs={2}
                        className={clsx(commonClasses.typo15_600, classes.gridCenter)}
                    >
                        Tax
                    </Grid>
                    <Grid
                        item
                        xs={2}
                        className={clsx(commonClasses.typo15_600, classes.gridCenter)}
                    >
                        Total
                    </Grid>
                    <Grid
                        item
                        xs={1}
                    ></Grid>
                </Grid>

                <div className={classes.tableList}>
                    {orderItems.map(orderItem => {
                        return (
                            <SingleSaleTableItem
                                item={orderItem}
                                errors={props.errors}
                                key={orderItem.orderItem.uuid}
                                selectedProductIds={orderProductIds}
                                selectedAddOnServiceIds={orderAddOnServiceIds}
                                onChange={changeHandler}
                                onRemove={removeHandler}
                            />
                        );
                    })}
                </div>
            </div>
        </>
    );
};
